import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { formatDisplayDate, formatDisplayDateTime } from "utils/dateFormat";
import { useNavigate } from "react-router-dom";
import {
  clientDisplayLabel,
  formatAppointmentDuration,
  formatAppointmentTimeDisplay,
  isPrivateAppointment,
  parseApptDate,
  participantNames,
  statusColor,
  visibilityLabel,
} from "./appointmentUtils";

const DetailRow = ({ label, children }) => {
  if (!children) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>
        {children}
      </Typography>
    </Box>
  );
};

const AppointmentDetailDialog = ({
  open,
  appointment,
  onClose,
  onEdit,
  onDeleteRequest,
  onSendReminder,
  isSendingReminder,
}) => {
  const navigate = useNavigate();
  if (!appointment) return null;

  const inactive = ["CANCELLED", "COMPLETED"].includes(appointment.status);
  const parsedDate = parseApptDate(appointment);
  const apptDate = formatDisplayDate(parsedDate);
  const timeDisplay = formatAppointmentTimeDisplay(appointment) || "—";
  const duration = formatAppointmentDuration(appointment);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {appointment.title || "Appointment"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {clientDisplayLabel(appointment)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
            <Chip
              label={visibilityLabel(appointment.visibility)}
              size="small"
              variant="outlined"
              icon={isPrivateAppointment(appointment) ? <LockIcon /> : undefined}
            />
            <Chip
              label={appointment.status}
              size="small"
              color={statusColor[appointment.status] || "default"}
              variant="outlined"
            />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <DetailRow label="APPOINTMENT DATE">{apptDate}</DetailRow>
            <DetailRow label="TIME">
              {timeDisplay}
              {duration && (
                <Typography variant="caption" display="block" color="text.secondary">
                  ({duration})
                </Typography>
              )}
            </DetailRow>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <DetailRow label="WITH">
              {appointment.withUserName || "—"}
              {appointment.withUserEmail && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {appointment.withUserEmail}
                </Typography>
              )}
            </DetailRow>
            <DetailRow label="CREATED BY">{appointment.createdByName || "—"}</DetailRow>
            <DetailRow label="REMINDER SENT">
              {appointment.reminderSentAt
                ? formatDisplayDateTime(appointment.reminderSentAt)
                : "Not sent"}
            </DetailRow>
          </Stack>
          {participantNames(appointment).length > 1 && (
            <DetailRow label="CLIENTS">{participantNames(appointment).join(", ")}</DetailRow>
          )}
          <DetailRow label="LOCATION">{appointment.location}</DetailRow>
          <DetailRow label="MEET LINK">
            {appointment.meetLink ? (
              <a
                href={appointment.meetLink}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline break-all"
              >
                {appointment.meetLink}
              </a>
            ) : null}
          </DetailRow>
          <DetailRow label="CC">{appointment.ccEmails}</DetailRow>
          {appointment.description && (
            <>
              <Divider />
              <DetailRow label="DESCRIPTION">{appointment.description}</DetailRow>
            </>
          )}
          {appointment.notes && (
            <>
              <Divider />
              <DetailRow label="NOTES">
                <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
                  {appointment.notes}
                </Box>
              </DetailRow>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, flexWrap: "wrap", gap: 1 }}>
        {appointment.clientId ? (
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            onClick={() => {
              onClose();
              navigate(`/admin/client/view/${appointment.clientId}`);
            }}
          >
            View client
          </Button>
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        {onSendReminder ? (
          <Tooltip title={inactive ? `Not available for ${appointment.status}` : "Send reminder"}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<SendIcon />}
                disabled={inactive || isSendingReminder}
                onClick={() => onSendReminder(appointment.id)}
              >
                Reminder
              </Button>
            </span>
          </Tooltip>
        ) : null}
        {onEdit ? (
          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={onEdit}>
            Edit
          </Button>
        ) : null}
        {onDeleteRequest ? (
          <IconButton size="small" color="error" onClick={() => onDeleteRequest(appointment)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        ) : null}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentDetailDialog;
